/**
 * CustomComponentLoader — Lazy-loads a tenant-specific custom component
 * when the render mode is 'custom'.
 *
 * The `customComponent` path stored in the DB is resolved to a dynamic
 * import via Vite's glob import mechanism. If the module is not found
 * or fails to load, the fallback component is rendered instead.
 *
 * Usage:
 *   const { renderMode, customComponent } = useRenderMode({ ... });
 *
 *   return (
 *     <CustomComponentLoader
 *       customComponent={customComponent}
 *       renderMode={renderMode}
 *       fallback={<StaticBudgetForm {...props} />}
 *       componentProps={props}
 *     />
 *   );
 *
 * Custom component modules must be placed under:
 *   app/uptk/modules/{domain}/{entity}/{portalType}/custom/
 *
 * And registered in the DB as:
 *   customComponent = "{domain}/{entity}/{portalType}/custom/{ComponentName}"
 *
 * Example DB entry:
 *   customComponent = "finance/budget/admin_portal/custom/BudgetFormCustom"
 *
 * Resolves to:
 *   import('@/uptk/modules/finance/budget/admin_portal/custom/BudgetFormCustom')
 */
import type { ComponentType} from 'react';
import { useIntl } from '@/react-intl';
import React, { Suspense, lazy, useMemo } from 'react';
import type { UiRenderMode } from '@/types/ui_render_mode';
import { SpectrumLoader } from '@/uptk/core/packages/spectrum-loader';

// ─── Types ──────────────────────────────────────────────────────

export type CustomComponentLoaderProps = {
  /** Path from DB: e.g. "finance/budget/admin_portal/custom/BudgetFormCustom" */
  customComponent: string | null;
  /** Current render mode from useRenderMode */
  renderMode: UiRenderMode;
  /** Fallback component rendered when mode is not 'custom' or loading fails */
  fallback: React.ReactNode;
  /** Props passed through to the custom component */
  componentProps?: Record<string, unknown>;
};

export type CustomComponentErrorProps = {
  error: Error;
  customComponent: string;
  onFallback: () => void;
};

// ─── Error Boundary ─────────────────────────────────────────────

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

class CustomComponentErrorBoundary extends React.Component<
  { children: React.ReactNode; customComponent: string; onFallback: () => void },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error(
      `[CustomComponentLoader] Failed to load custom component "${this.props.customComponent}":`,
      error,
      info,
    );
    // Trigger fallback on next render
    this.props.onFallback();
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return null; // Error boundary triggers onFallback via componentDidCatch
    }
    return this.props.children;
  }
}

// ─── Custom Component Registry ──────────────────────────────────
// Pre-built lazy component cache keyed by path string.
// This avoids re-creating React.lazy() on every render, which would
// cause Suspense to re-trigger.

const lazyCache = new Map<string, React.LazyExoticComponent<ComponentType<Record<string, unknown>>>>();

/**
 * Resolve a custom component path to a lazy-loaded React component.
 *
 * Path format: "finance/budget/admin_portal/custom/BudgetFormCustom"
 * Resolves to: import('@/uptk/modules/finance/budget/admin_portal/custom/BudgetFormCustom')
 *
 * The actual .tsx file must exist at the resolved path for Vite to
 * bundle it as a dynamic chunk.
 */
function resolveLazyComponent(
  customComponent: string,
): React.LazyExoticComponent<ComponentType<Record<string, unknown>>> {
  const cached = lazyCache.get(customComponent);
  if (cached) return cached;

  // Build the dynamic import path
  // Vite supports dynamic imports with string template literals
  const modulePath = `@/uptk/modules/${customComponent}`;

  const LazyComponent = lazy(() =>
    import(/* @vite-ignore */ modulePath)
      .then((module) => {
        // Support both default and named exports
        const Component = module.default ?? module[customComponent.split('/').pop() ?? 'default'];
        if (typeof Component !== 'function' && typeof Component !== 'object') {
          throw new Error(
            `Custom component "${customComponent}" does not export a valid React component`,
          );
        }
        return { default: Component as ComponentType<Record<string, unknown>> };
      })
      .catch((err) => {
        console.error(`[CustomComponentLoader] Dynamic import failed for "${customComponent}":`, err);
        // Compute the technical detail outside JSX and hand it to the designed
        // CustomComponentErrorDisplay (the ErrorState equivalent for failed custom
        // component loads) rather than rendering a raw error.message in place.
        const errorText = err instanceof Error ? err.message : String(err);
        // Return a fallback component that signals the error
        return {
          default: () => (
            <CustomComponentErrorDisplay
              customComponent={customComponent}
              error={errorText}
            />
          ),
        };
      }),
  );

  lazyCache.set(customComponent, LazyComponent);
  return LazyComponent;
}

// ─── Error Display ──────────────────────────────────────────────

function CustomComponentErrorDisplay({
  customComponent,
  error,
}: {
  customComponent: string;
  error: string;
}): React.ReactElement {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  return (
    <div
      style={{
        padding: '16px',
        margin: '8px',
        border: '1px solid #ff4444',
        borderRadius: '4px',
        backgroundColor: '#2e1a1a',
        color: '#ff8888',
        fontFamily: 'monospace',
        fontSize: '13px',
      }}
    >
      <strong>{t('core.customLoader.title')}</strong>
      <br />
      {t('core.customLoader.pathLabel')} <code>{customComponent}</code>
      <br />
      {t('core.customLoader.errorLabel')} <code>{error}</code>
      <br />
      <small>{t('core.customLoader.fallback')}</small>
    </div>
  );
}

// ─── Loading Fallback ───────────────────────────────────────────

function CustomComponentLoadingFallback(): React.ReactElement {
  return (
    <div
      style={{
        padding: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SpectrumLoader />
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────

/**
 * CustomComponentLoader — Renders a lazy-loaded custom component
 * when renderMode is 'custom', otherwise renders the fallback.
 *
 * Features:
 * - React.lazy() + Suspense for code splitting
 * - Error boundary that auto-falls back to static on load failure
 * - Lazy component cache (avoids re-triggering Suspense)
 * - Loading spinner while chunk loads
 * - Typed props passthrough
 */
export function CustomComponentLoader({
  customComponent,
  renderMode,
  fallback,
  componentProps = {},
}: CustomComponentLoaderProps): React.ReactElement {
  const [useFallback, setUseFallback] = React.useState(false);

  // If not in custom mode, or no path provided, render fallback immediately
  if (renderMode !== 'custom' || !customComponent || useFallback) {
    return <>{fallback}</>;
  }

  return (
    <CustomComponentErrorBoundary
      customComponent={customComponent}
      onFallback={() => setUseFallback(true)}
    >
      <Suspense fallback={<CustomComponentLoadingFallback />}>
        <CustomComponentRenderer
          customComponent={customComponent}
          componentProps={componentProps}
        />
      </Suspense>
    </CustomComponentErrorBoundary>
  );
}

// ─── Inner Renderer ─────────────────────────────────────────────
// Separated so the lazy component is only resolved when actually needed.

function CustomComponentRenderer({
  customComponent,
  componentProps,
}: {
  customComponent: string;
  componentProps: Record<string, unknown>;
}): React.ReactElement {
  const LazyComponent = useMemo(
    () => resolveLazyComponent(customComponent),
    [customComponent],
  );

  return <LazyComponent {...componentProps} />;
}

// ─── Hook: useCustomComponent ───────────────────────────────────

export type UseCustomComponentResult = {
  /** Whether to render the custom component */
  isActive: boolean;
  /** The resolved lazy component, or null */
  Component: ComponentType<Record<string, unknown>> | null;
  /** Loading state */
  loading: boolean;
  /** Error if the custom component failed to resolve */
  error: Error | null;
};

/**
 * Hook that resolves a custom component path into a lazy-loaded component.
 *
 * Lower-level than CustomComponentLoader — use this when you need
 * fine-grained control over the rendering (e.g., in a grid column
 * cell renderer).
 *
 * Usage:
 *   const { renderMode, customComponent } = useRenderMode({ ... });
 *   const { isActive } = useCustomComponent(customComponent, renderMode);
 *
 *   if (isActive) {
 *     return <CustomComponentLoader ... />;
 *   }
 *   return <StaticComponent ... />;
 */
export function useCustomComponent(
  customComponent: string | null,
  renderMode: UiRenderMode,
): UseCustomComponentResult {
  const isActive = renderMode === 'custom' && !!customComponent;

  return useMemo(
    () => ({
      isActive,
      Component: isActive ? resolveLazyComponent(customComponent!) : null,
      loading: false, // Suspense handles loading
      error: null,
    }),
    [isActive, customComponent, renderMode],
  );
}

// ─── Clear cache (useful for hot reload / testing) ──────────────

export function clearCustomComponentCache(): void {
  lazyCache.clear();
}
